import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { RefAttributes } from 'react';

import styles from './styles.module.scss';
import Button from '../../Button/index.ts';
import type { Variant, InferPropTypes } from '../../index.js';
import addVariants from '../../utils/addVariants/index.ts';

export const COLORS = ['dark', 'black'] as const;
export const VARIANTS = ['default', 'no-padding', 'comfortable', 'active', ...COLORS] as const;
const propTypes = {
  /**
   * Fired every time the list item is clicked
   */
  onClick: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  /**
   * Renders the list item as a link
   */
  href: PropTypes.string,
  /**
   * The list item content itself
   */
  children: PropTypes.node,
  /**
   * Decides the variant of the button
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf(VARIANTS),
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.oneOf(VARIANTS), PropTypes.bool]))
  ]),
  /**
   * Additional classnames to apply
   */
  className: PropTypes.string,
  /**
   * Adds a divider to the bottom of the list item
   */
  hasDivider: PropTypes.bool,
  isSelected: PropTypes.bool,
  isDisabled: PropTypes.bool
};
const defaultProps = {
  className: '',
  variant: 'default',
  onClick: null,
  children: null,
  href: '',
  hasDivider: false,
  isSelected: false,
  isDisabled: false
};
export type ListItemProps = InferPropTypes<
  typeof propTypes,
  typeof defaultProps,
  {
    variant: Variant<(typeof VARIANTS)[number]>;
    onClick: React.MouseEventHandler<HTMLElement> | false | null | undefined;
    isSelected: boolean | undefined | null;
    href: string | false | undefined | null;
  }
> &
  RefAttributes<HTMLLIElement>;
const ListItem = React.forwardRef<HTMLLIElement, ListItemProps>(
  (
    {
      children,
      variant: variants,
      href,
      onClick,
      className,
      hasDivider,
      isSelected,
      isDisabled,
      ...rest
    },
    ref
  ) => {
    const computedClassName = classnames(
      addVariants(
        'aglmd-list-item',
        styles,
        variants,
        hasDivider && 'divider',
        isSelected && 'active',
        isDisabled && 'disabled'
      ),
      className
    );

    if (href || onClick) {
      return (
        <Button
          component="li"
          href={href || undefined}
          onClick={onClick || undefined}
          variant={['flat', 'square']}
          color={
            COLORS.filter(c =>
              ([] as (string | undefined | boolean)[]).concat(variants).includes(c)
            )[0] || 'blue-hover'
          }
          isDisabled={isDisabled}
          ref={ref}
          className={computedClassName}
          {...rest}
        >
          {children}
        </Button>
      );
    }
    return (
      <li {...rest} className={computedClassName} ref={ref}>
        {children}
      </li>
    );
  }
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(ListItem as any).propTypes = propTypes;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(ListItem as any).defaultProps = defaultProps;

export default ListItem;
